import React, { useState, useEffect, useRef } from "react";
import { Grid, ListItemSecondaryAction } from "@material-ui/core";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Slider,
} from "@mui/material";
import CustomModal from "../../components/common/customModal";
import upload from "../../assets/images/CloudUpload.svg";
import DeleteIcon from "../../assets/images/deleteIconRed.svg";
import CustomSelect from "../../components/common/customSelect";
import { showToast } from "../../components/common/toastNotification";
import dashboardService from "../../services/DashboardServices";
import { BannerImage } from "../../types/dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Cropper, { Area } from "react-easy-crop";
import getCroppedImg, { CropArea } from "../../helper/cropImage";
import CustomInput from "../../components/common/inputField";

interface BannerImageModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  bannerImageList: BannerImage[];
}

interface FileData {
  id?: number | undefined;
  bannerName: string;
  bannerUrl: string;
  fileSize: string;
}

const BannerImageModal: React.FC<BannerImageModalProps> = ({
  open,
  onClose,
  onSave,
  bannerImageList,
}) => {
  const allowedFileTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ];
  const [isExpanded, setIsExpanded] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<FileData[]>([]);
  const [isDeletedFiles, setIsDeletedFiles] = useState<FileData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [durationObj, setDurationObj] = useState<{
    id: number;
    key: string;
    value: string;
  }>({
    id: 0,
    key: "",
    value: "",
  });
  const [selectedDuration, setSelectedDuration] = useState("5seconds");
  const [croppingQueue, setCroppingQueue] = useState<File[]>([]);
  const [currentCroppingImage, setCurrentCroppingImage] = useState<
    string | null
  >(null);
  const [currentCroppingFileName, setCurrentCroppingFileName] =
    useState<string>("");
  const [url, setUrl] = useState<string>("");

  useEffect(() => {
    if (open) {
      fetchDurationObject();
      if (bannerImageList.length > 0) {
        setUploadedFiles(bannerImageList);
      }
    }
    setIsDeletedFiles([]);
  }, [open, bannerImageList]);
  useEffect(() => {
    if (!currentCroppingImage && croppingQueue.length > 0) {
      const nextFile = croppingQueue[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentCroppingImage(reader.result as string);
        setCurrentCroppingFileName(nextFile.name);
      };
      reader.readAsDataURL(nextFile);
    }
  }, [croppingQueue, currentCroppingImage]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files ? Array.from(event.target.files) : [];
    const validFiles = newFiles.filter((file) =>
      allowedFileTypes.includes(file.type)
    );

    if (validFiles.length !== newFiles.length) {
      showToast(
        "Only image files (JPEG, JPG, PNG, GIF, WEBP) are allowed.",
        "error"
      );
    }
    if (files.length > 0) {
      showToast("You can only upload one file at a time.", "error");
      return;
    }
    if (validFiles.length > 0) {
      setCroppingQueue((prev) => [...prev, ...validFiles]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCroppedImage = (blob: Blob) => {
    const file = new File([blob], currentCroppingFileName, { type: blob.type });

    const fileData: FileData = {
      bannerName: file.name,
      bannerUrl: URL.createObjectURL(blob),
      fileSize: `${(file.size / 1024).toFixed(1)} KB`,
    };

    setFiles((prev) => [...prev, file]);
    setUploadedFiles((prev) => [...prev, fileData]);
    setCroppingQueue((prev) => prev.slice(1));
    setCurrentCroppingImage(null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const validFiles = droppedFiles.filter((file) =>
      allowedFileTypes.includes(file.type)
    );

    if (validFiles.length !== droppedFiles.length) {
      alert("Only image files (JPEG, JPG, PNG, GIF, WEBP) are allowed.");
    }

    const fileDataWithUrls = validFiles.map((file) => ({
      bannerName: file.name,
      bannerUrl: URL.createObjectURL(file),
      fileSize: `${(file.size / 1024).toFixed(1)} KB`,
    }));

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    setUploadedFiles((prevFiles) => [...prevFiles, ...fileDataWithUrls]);
  };

  const handleDelete = (index: number) => {
    const tempDeletedFiles = uploadedFiles.filter((file) => file.id === index);

    if (tempDeletedFiles.length > 0) {
      const fileToRemove = tempDeletedFiles[0];

      setUploadedFiles((prevFiles) =>
        prevFiles.filter((file) => file.id !== index)
      );

      setFiles((prevFiles) =>
        prevFiles.filter(
          (file) =>
            file.name !== fileToRemove.bannerName || // Check by name as it might not have id
            `${(file.size / 1024).toFixed(1)} KB` !== fileToRemove.fileSize
        )
      );

      if (fileToRemove.id !== undefined) {
        setIsDeletedFiles((prev) => [...prev, fileToRemove]);
      }
    } else {
      setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    }
  };

  // handle Drop down onchange code
  const handleDurationOnChange = (event: {
    target: { name: string; value: number | string };
  }) => {
    setSelectedDuration(String(event.target.value));
  };

  // handle form validation code
  const validateForm = () => {
    let fileErrors = false;

    if (files.length === 0) {
      showToast("Please upload the banner images", "error");
      fileErrors = false;
    } else {
      fileErrors = true;
    }

    return fileErrors;
  };

  // Fetch Duration object for Banner
  const fetchDurationObject = async () => {
    try {
      const response = await dashboardService.getDurationtimeAPI();
      const finalReponse = response?.data?.data;
      if (finalReponse.value !== "") {
        setDurationObj({
          id: finalReponse.id,
          key: finalReponse.key,
          value: finalReponse.value,
        });
        setSelectedDuration(finalReponse.value);
      } else {
        setDurationObj({
          id: 0,
          key: "",
          value: "",
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // handle save banner data
  const handleSaveData = async () => {
    const isOnlyDurationChanged =
      uploadedFiles.length === bannerImageList.length &&
      files.length === 0 &&
      isDeletedFiles.length === 0 &&
      selectedDuration !== durationObj.value;

    if (isOnlyDurationChanged) {
      await addUpdateBannerDurationTime();
      showToast("Banner duration updated successfully", "success");
      onSave();
      onClose();
      return;
    }

    if (uploadedFiles.length === 0 && files.length === 0) {
      if (!validateForm()) return;
    }

    try {
      const formDataToSend = new FormData();

      if (files.length > 0) {
        formDataToSend.append("ObjReq", files[0]);
      }

      if (uploadedFiles.length > 0) {
        formDataToSend.append("urls[]", url);
      } else if (url) {
        formDataToSend.append("urls[]", url);
      } else {
        formDataToSend.append("urls[]", "");
      }

      isDeletedFiles.forEach((deletedFile) => {
        if (deletedFile.id !== undefined) {
          formDataToSend.append("DeletedBannerIds", String(deletedFile.id));
        }
      });

      const response = await dashboardService.addUpdateBannerDataAPI(
        formDataToSend
      );

      if (response.data.statusCode === 200 && response.data.success) {
        showToast(response.data.message, "success");

        await addUpdateBannerDurationTime();

        setFiles([]);
        setIsDeletedFiles([]);
        setUploadedFiles(response.data.uploadedFiles);

        onSave();
        onClose();
      } else {
        showToast(response?.data?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error saving Banner data", error);
      showToast("Error saving banner data.", "error");
    }
  };

  // fetch addUpdateBannerDurationTime list
  const addUpdateBannerDurationTime = async () => {
    const payload = {
      key: "BannerSliderTimeout",
      value: selectedDuration,
      id: durationObj?.id ?? 1,
    };
    try {
      const response =
        durationObj.value === selectedDuration
          ? await dashboardService.updateDurationtimeAPI(payload)
          : await dashboardService.addDurationtimeAPI(payload);
      const finalResponse = response.data.data;

      // eslint-disable-next-line no-console
      console.log(finalResponse);
    } catch (error) {
      console.error("Error:", error);
    }
  };
  {
    /* crop image functionality code */
  }
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea>();

  const onCropChange = (newCrop: { x: number; y: number }) => setCrop(newCrop);
  const onCropCompleteCallback = (_: Area, croppedArea: CropArea) =>
    setCroppedAreaPixels(croppedArea);

  const onCropDone = async () => {
    if (currentCroppingImage && croppedAreaPixels) {
      try {
        const croppedImageBlob = await getCroppedImg(
          currentCroppingImage,
          croppedAreaPixels
        );
        handleCroppedImage(croppedImageBlob);
      } catch (error) {
        console.error("Cropping failed:", error);
        showToast("Something went wrong while cropping the image.", "error");
      }
    }
  };

  const handelCancelCropImage = () => {
    setCroppingQueue((prev) => prev.slice(1));
    setCurrentCroppingImage(null);
  };

  return (
    <>
      {" "}
      <CustomModal
        open={open}
        handleClose={onClose}
        title="Upload Banner Image"
        onSave={currentCroppingImage ? onCropDone : handleSaveData}
        saveButtonName={currentCroppingImage ? "Crop" : "Upload"}
        {...(currentCroppingImage && {
          button2: "Cancel",
          onClickButton2: handelCancelCropImage,
        })}
        width={800}
      >
        {currentCroppingImage ? (
          <Box>
            <Box position="relative" width="100%" height={300} bgcolor="#333">
              <Cropper
                image={currentCroppingImage}
                crop={crop}
                zoom={zoom}
                aspect={15 / 5}
                onCropChange={onCropChange}
                onZoomChange={setZoom}
                onCropComplete={onCropCompleteCallback}
              />
            </Box>
            <Box
              p={2}
              sx={{
                overflow: "visible",
                position: "relative",
              }}
            >
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, z) => setZoom(z as number)}
                sx={{
                  color: "#fff",
                  height: 6,
                  "& .MuiSlider-thumb": {
                    height: 20,
                    width: 20,
                    marginLeft: "10px",
                    backgroundColor: "#fff",
                    border: "2px solid currentColor",
                    "&:hover": {
                      boxShadow: "0 0 0 8px rgba(255,255,255,0.16)",
                    },
                    "&.Mui-active": {
                      boxShadow: "0 0 0 14px rgba(255,255,255,0.16)",
                    },
                    "&.Mui-focusVisible": {
                      boxShadow: "0 0 0 14px rgba(255,255,255,0.16)",
                    },
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "#fff",
                  },
                  "& .MuiSlider-rail": {
                    opacity: 0.3,
                    backgroundColor: "#fff",
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragging ? "#f1f1f1" : "#f9f9f9",
                    maxWidth: "750px",
                    margin: "auto",
                    display: "flex",
                    bgcolor: "#212121",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "234px",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    // multiple
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                  <img src={upload} alt="Upload Icon" />

                  <Typography
                    variant="h6"
                    sx={{ mt: 2, fontSize: "16px", fontWeight: "500" }}
                  >
                    Drop your files here or{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "red",
                        fontWeight: "bold",
                        cursor: "pointer",
                        fontFamily: "Montserrat",
                      }}
                    >
                      browse
                    </Box>
                  </Typography>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontSize: "14px", mt: 1, color: "#E6E6E6" }}
                  >
                    Max. File Size: 200MB
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: "12px", mt: 1, color: "#E6E6E6" }}
                  >
                    Recommended image aspect ratio: 15:5 (e.g., 1500×500 px)
                  </Typography>
                </Box>

                <Grid container spacing={2} style={{ marginTop: "10px" }}>
                  <Grid item xs={12} md={6}>
                    <CustomSelect
                      name="sliderDuration"
                      label="Slider Duration"
                      placeHolder="Select Slider Duration"
                      value={selectedDuration}
                      onChange={handleDurationOnChange}
                      options={[
                        { label: "5 Seconds", value: "5seconds" },
                        { label: "10 Seconds", value: "10seconds" },
                        { label: "15 Seconds", value: "15seconds" },
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomInput
                      label="URL"
                      type="text"
                      name="URL"
                      placeholder="Enter URL"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Grid style={{ marginTop: "10px" }}>
                  <Box
                    px={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ cursor: "pointer", color: "#fff" }}
                  >
                    <Typography
                      className="f-12"
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        gap: "5px",
                      }}
                    >
                      All Files{" "}
                      {isExpanded ? (
                        <ExpandLessIcon sx={{ color: "#fff" }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: "#fff" }} />
                      )}
                    </Typography>
                  </Box>
                </Grid>

                {/* Uploaded Files List */}
                {isExpanded &&
                  (Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
                    <List sx={{ mt: 1, width: "100%" }}>
                      {uploadedFiles.map((item) => (
                        <ListItem
                          key={item.id}
                          sx={{ borderBottom: "1px solid #808080", px: 1 }}
                        >
                          <img
                            src={item.bannerUrl}
                            alt="Uploaded Preview"
                            style={{
                              width: "70px",
                              height: "42px",
                              objectFit: "cover",
                              paddingRight: "10px",
                            }}
                          />
                          <ListItemText
                            primary={
                              <Typography
                                noWrap
                                sx={{
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: "450px",
                                  display: "block",
                                }}
                              >
                                {item.bannerName}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                color: "#CCCCCCCC !important",
                              }}
                            >
                              <ListItemText
                                sx={{
                                  "& .MuiTypography-root": {
                                    color: "#CCCCCC !important",
                                  },
                                }}
                                secondary={item.fileSize}
                              />
                              <IconButton
                                sx={{
                                  "&:hover": { backgroundColor: "transparent" },
                                }}
                                edge="end"
                                onClick={() =>
                                  item.id !== undefined && handleDelete(item.id)
                                }
                              >
                                <img
                                  src={DeleteIcon}
                                  alt="Delete"
                                  style={{ marginLeft: "8px" }}
                                  width={20}
                                  height={20}
                                />
                              </IconButton>
                            </div>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : files.length > 0 ? (
                    <List sx={{ mt: 2, width: "100%" }}>
                      {files.map((file, index) => (
                        <ListItem
                          key={index}
                          sx={{ borderBottom: "1px solid #808080" }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Uploaded Preview"
                            style={{
                              width: "70px",
                              height: "70px",
                              objectFit: "cover",
                              paddingRight: "10px",
                            }}
                          />
                          <ListItemText
                            sx={{
                              "& .MuiTypography-root": {
                                color: "#CCCCCC !important",
                              },
                            }}
                            primary={file.name}
                          />
                          <ListItemSecondaryAction>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                color: "#CCCCCC !important",
                              }}
                            >
                              <ListItemText
                                sx={{
                                  "& .MuiTypography-root": {
                                    color: "#CCCCCC !important",
                                  },
                                }}
                                secondary={`${(file.size / 1024).toFixed(
                                  1
                                )} KB`}
                              />
                              <IconButton
                                sx={{
                                  "&:hover": { backgroundColor: "transparent" },
                                }}
                                edge="end"
                                onClick={() => handleDelete(index)}
                              >
                                <img
                                  src={DeleteIcon}
                                  alt="Delete"
                                  style={{ marginLeft: "10px" }}
                                  width={20}
                                  height={20}
                                />
                              </IconButton>
                            </div>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : null)}
              </Grid>
            </Grid>
          </Box>
        )}
      </CustomModal>
    </>
  );
};

export default BannerImageModal;
